/**
 * The `/reservation/*` routes reservations v2 calls (RESERVATIONS_V2_PLAN.md §4.6, RV1c), held to
 * the backend snapshot by `api/routes.test.ts`.
 *
 * The `Wire…` types are the backend's own shapes, as read from its code
 * (`docs/architecture/reservations/backend.md`). They are NOT v2's domain types (those come later,
 * in `reservations/model`). Where the backend stores fields v2 does not model, the type has an
 * index signature, because a save replaces the whole document: a field v2 drops is deleted, so v2
 * must send every field it was given back.
 *
 * `api/` is an app-wide piece and imports nothing from `reservations/` or `tables/`, so the wire
 * shapes live here, as `item.ts` does for its stock answer.
 */
import { gated } from "../../capabilities/access";
import { defineRoutes, route } from "../route";

/** a person's name as the backend stores it */
export interface WireName {
  /** the guest's first name */
  firstName?: string;
  /** the guest's last name */
  lastName?: string;
}

/** how to reach the guest of a booking */
export interface WireContactInfo {
  /** the guest's name */
  name?: WireName;
  /** the guest's phone number, without country code (the SMS route keeps only 10-digit numbers) */
  phoneNumber?: string;
  /** the country the phone number belongs to, as a code such as `IN` */
  phoneCountryCode?: string;
  /** the phone's dialling prefix, such as `+91` */
  phonePrefix?: string;
  /** the guest's email, used by the link-email route when none is sent */
  email?: string;
  /** any other contact field the backend stores; sent back unchanged on a save */
  [field: string]: unknown;
}

/** a table named on a service line of a booking */
export interface WireLineTable {
  /** the table's id (a table is a "resource" to the backend) */
  resourceId: string;
  /** the table's name when the booking was saved */
  resourceName?: string;
  /** the name of the section the table sits in, such as "Terrace" */
  resourceGroupName?: string;
  /** any other table field the backend stores; sent back unchanged on a save */
  [field: string]: unknown;
}

/** one history entry of a booking or of one of its lines; a cancel's reason is kept only here (backend.md §2.D) */
export interface WireEvent {
  /** the status the entry records, such as `Created`, `Updated` or `Cancelled` */
  status: string;
  /** who did it: user, device and date, as the backend writes them */
  eventBy?: Record<string, unknown>;
  /** why, when a reason was given (a cancel's reason lives only on its line's entry) */
  reason?: string;
  /** any other event field the backend stores; sent back unchanged on a save */
  [field: string]: unknown;
}

/**
 * A booking's time window, status and tables. A POS booking has exactly one line, and every backend
 * reader reads line 0 (backend.md §7.2).
 */
export interface WireServiceLine {
  /** the line's id, used to cancel or seat one line of a booking */
  uid?: string;
  /** when the booking starts, `YYYY-MM-DDTHH:mm` on the branch's wall clock (no zone) */
  start: string;
  /** when the booking ends, in the same format as `start` */
  end: string;
  /** the line's status: `Tentative`, `Confirmed` or `Cancelled` */
  status: string;
  /** how far the visit has got: `Checked In`, `In Process`, `Notified` or `Completed`; empty until a terminal or a sale sets it */
  fulfillmentStatus?: string;
  /** who last changed the fulfilment status, as the backend writes it */
  fulfilledBy?: Record<string, unknown>;
  /** the tables the booking holds; empty when no table is assigned yet */
  resourceList?: WireLineTable[];
  /** the service booked (used by appointment mode; a table booking has none) */
  serviceId?: string;
  /** the name of the service booked */
  serviceName?: string;
  /** the line's own history */
  eventLog?: WireEvent[];
  /** any other line field the backend stores; sent back unchanged on a save */
  [field: string]: unknown;
}

/**
 * The whole stored booking, as `fetch` answers it and `save` takes it. A create has no `mid` or
 * `version`; an update sends both back with everything else it was given (backend.md §3.A, §3.E).
 */
export interface WireReservation {
  /** the booking's id; absent on a create */
  mid?: string;
  /** the booking's version for the optimistic-concurrency check; absent on a create, a number on an update, bumped by the server on every write */
  version?: number;
  /** the branch the booking belongs to; it cannot change on an update (backend.md §3.B) */
  branchId: string;
  /** `POS` or `Online`; the partner is `source`, not `channel` (backend.md O5) */
  channel: string;
  /** the number of guests */
  count: number;
  /** the booking's lines; a POS booking has exactly one */
  serviceList: WireServiceLine[];
  /** the short number shown to people; the server sets it */
  reservationNumber?: string;
  /** the partner's key in lower case (`zomato`, `swiggy`, `dotpe`, …); empty for the POS's own bookings */
  source?: string;
  /** `"external"` for a partner's booking; otherwise empty or `null` */
  sourceLabel?: string | null;
  /** how to reach the guest */
  contactInfo?: WireContactInfo;
  /** the guest's customer record in Rista, when linked */
  customerRefId?: string;
  /** the guest's customer id, when linked */
  customerId?: string;
  /** the guest's consumer id, when linked */
  consumerId?: string;
  /** free text the staff wrote about the booking */
  notes?: string;
  /** the booking's reference text */
  reference?: string;
  /** Rista's own record of the partner outlet the booking came through (partner bookings) */
  storeId?: string;
  /** the partner's own id for that outlet (partner bookings; `externalStoreId` on the partner's wire) */
  sourceStoreId?: string;
  /** the partner's own id for this booking (backend.md O6) */
  sourceReservationId?: string;
  /** where the backend reports back to a partner when the booking changes */
  callbackURL?: string;
  /** the sale opened for this booking, once the guest is seated */
  saleId?: string;
  /** the booking's own history */
  eventLog?: WireEvent[];
  /** any other booking field the backend stores; sent back unchanged on a save */
  [field: string]: unknown;
}

/** `fetch`'s answer: the booking plus the linked sale's status, "Open" while an order is open (backend.md O1, §3.J) */
export interface WireFetchedReservation extends WireReservation {
  /** the status of the sale named by `saleId`; missing when there is no sale or it could not be read */
  saleStatus?: string;
}

/** `save`'s answer: the stored booking with its new `version` (backend.md §3.G) */
export interface WireSaveAnswer {
  /** the booking as stored; keep its `version` for the next save */
  reservation: WireReservation;
}

/**
 * One row of `filter`'s answer: a trimmed booking, one row per service line. It has no `version`,
 * `sourceLabel`, `saleId` or `reference`, so a write fetches the booking first (backend.md §3.G).
 */
export interface WireReservationRow {
  /** the booking's id (`mid` on a fetched booking) */
  reservationId: string;
  /** the short number shown to people */
  reservationNumber?: string;
  /** `POS` or `Online` */
  channel?: string;
  /** the partner's key; empty for own bookings */
  source?: string;
  /** the number of guests */
  count?: number;
  /** how to reach the guest */
  contactInfo?: WireContactInfo;
  /** the guest's name as one string */
  customerName?: string;
  /** the guest's customer record in Rista, when linked */
  customerRefId?: string;
  /** the guest's consumer id, when linked */
  consumerId?: string;
  /** free text the staff wrote about the booking */
  notes?: string;
  /** where the backend reports back to a partner */
  callbackURL?: string;
  /** the one line this row stands for (the filter unwinds the booking's lines) */
  serviceList: WireServiceLine[];
  /** any other row field the backend returns */
  [field: string]: unknown;
}

/**
 * The filter's body as v2 sends it: the date alone returns every booking that starts or ends on it,
 * in every status, uncapped. v2 never sends `time` or `endTime`: that mode hides seated, completed
 * and cancelled bookings (backend.md O3).
 */
export interface WireDayFilter {
  /** the day to list, `YYYY-MM-DD` */
  date: string;
  /** the branch to list; the token's branch when left out */
  branchId?: string;
}

/** a slot's bookings by status and their guests */
export interface WireSlotCounts {
  /** the number of Tentative bookings in the slot */
  tentative: number;
  /** the number of Confirmed bookings in the slot (a Completed one counts here too) */
  confirmed: number;
  /** the number of Cancelled bookings in the slot */
  cancelled?: number;
  /** the guests across the Tentative bookings */
  tentativeCount: number;
  /** the guests across the Confirmed bookings */
  confirmedCount: number;
  /** the guests across the Cancelled bookings */
  cancelledCount?: number;
}

/** one slot of the day summary; `validSlot: false` is a row for a booking off the slot grid; `capacity` is seats, not seats left (backend.md §4.D) */
export interface WireSlotRow {
  /** the slot's start, `HH:mm` */
  slotTime: string;
  /** the day the summary was asked for */
  date?: string;
  /** `false` for the extra row of a booking that fits no slot */
  validSlot?: boolean;
  /** the seats the chosen tables hold in this slot (seats left = `capacity` − `confirmedCount` − `tentativeCount`) */
  capacity: number;
  /** the slot's bookings by status, with the same numbers per partner under `bySource` (own bookings under `"__none__"`) */
  actuals?: WireSlotCounts & { bySource?: Record<string, WireSlotCounts> };
  /** the names of the first few bookings in the slot */
  customerNames?: WireName[];
  /** the raw seats before partner holds; only when partner allocation is on */
  totalCapacity?: number;
  /** the seats held back for partners; only when partner allocation is on */
  allocatedCapacity?: number;
}

/** the day summary's body; always send `resourceId`, `"ALL"` for every table (backend.md §4.D) */
export interface WireDaySummaryRequest {
  /** the day to summarise, `YYYY-MM-DD` */
  date: string;
  /** `"ALL"`, `"UNASSIGNED"` or one table's id */
  resourceId: string;
  /** the branch to summarise; the token's branch when left out */
  branchId?: string;
}

/**
 * The settings row that applies to a branch. The backend does not check the minutes are numbers, so
 * a reader coerces them; `tableReservation` counts only on a row that names the branch (backend.md §4.B, §4.C).
 */
export interface WireReservationSettings {
  /** the row's id */
  mid?: string;
  /** the row's label (`"Default"` for the business-level row the backend creates on a read) */
  name?: string;
  /** the branches the row covers; empty or absent means the business-level default */
  branchIds?: string[];
  /** minutes between slot starts */
  interval: number | string;
  /** the usual length of a booking in minutes; a hint for the form's default, no server code reads it */
  avgReservationTime: number | string;
  /** minutes before closing within which no slot starts */
  lastReservationOffset: number | string;
  /** minutes ahead a booked table shows as "reserved"; read only on a row that names the branch */
  tableReservation?: number | string;
  /** the percentage of a slot's seats held back from online channels */
  onlineBufferPercentage?: number;
  /** `Capacity` or `Resource`, worked out on read from the business's appointment mode */
  reservationMode?: string;
  /** the kind of bookable thing, worked out on read */
  resourceType?: string;
  /** the word for it ("Tables" or "Specialists"), worked out on read */
  resourceLabel?: string;
  /** the terms the guest sees on the booking page */
  terms?: string;
  /** any other settings field the backend stores */
  [field: string]: unknown;
}

/** the fetch body: which booking to read */
export interface WireFetchRequest {
  /** the booking's id */
  reservationId: string;
  /** the booking's branch; without it the backend looks in the token's branch only */
  branchId?: string;
}

/** the cancel body; the booking's id is in the path */
export interface WireCancelRequest {
  /** the booking's OWN branch: another branch's answers `REST_INVALID_RESERVATION` (backend.md §2.D) */
  branchId: string;
  /** the one line to cancel; without it every line is cancelled */
  uid?: string;
  /** why it was cancelled; kept only on the cancelled line's history entry */
  reason?: string;
}

/** the SMS body; the booking's id is in the path */
export interface WireSmsRequest {
  /** the guest's phone number, 10 digits (other lengths are dropped silently) */
  mobileNumber: string;
  /** the text to send; it holds one `%s` where the backend puts the booking link (backend.md §5.1) */
  message: string;
  /** whether the business has an SMS provider; when false nothing is sent and the answer carries the text to send by hand */
  hasSMSProvider?: boolean;
  /** the branch to send as; the token's branch when left out */
  branchId?: string;
}

/** the WhatsApp body; the booking's id is in the path */
export interface WireWhatsAppRequest {
  /** the guest's phone number */
  mobileNumber: string;
  /** required by the backend but never used: the template carries the words */
  message: string;
  /** the brand whose name goes in the message; the backend refuses a missing or unknown brand */
  brandId: string;
}

/** the email-the-link body; the booking's id is in the path */
export interface WireLinkEmailRequest {
  /** where to send; the booking's own email when left out (neither gives an error) */
  email?: string;
  /** the branch to send as */
  branchId?: string;
  /** which email to send; `CONSUMER_EMAIL` when left out */
  type?: "CONSUMER_EMAIL" | "BUSINESS_EMAIL" | "CONSUMER_REMINDER_EMAIL";
}

/** what the SMS and WhatsApp routes answer; `sent` is only a hint, not a delivery receipt (backend.md §5.1) */
export interface WireSendAnswer {
  /** whether the backend handed the message on; a false positive when no provider or a bad number */
  sent?: unknown;
  /** the text the client can send itself when `sent` is false */
  message?: string;
}

/** the id every route that acts on one booking takes in its path */
type ReservationPath = { reservationId: string };

/** Every `/reservation/*` route reservations v2 calls, named by what it does. */
export const RESERVATION = defineRoutes("reservation", {
  /** the one settings row that applies to a branch */
  settings: route<undefined, WireReservationSettings>(
    "GET",
    "/reservation/settings",
    gated({ modules: ["Schedule", "Sale", "Order", "Settings", "Kiosk", "Marketing", "Inventory", "Support"] }),
    {
      notes:
        "send the query `branchId`: without it the backend answers the business-level row, never the branch's (backend.md §4.A, §4.B; reservation_controller.js:46-62, :254-270; reservation_settings_service.js:25-52). A read with no row at all creates the default row",
    },
  ),
  /** the day's slots with the bookings and seats in each */
  daySummary: route<WireDaySummaryRequest, WireSlotRow[]>("POST", "/reservation/summary/date", gated({ modules: ["Schedule", "Sale", "Support"] }), {
    notes:
      "sent as JSON until RV1j's capture says otherwise: v1 sends it form-encoded and the body parser reads both; send `resourceId` (`ALL`) always; the answer is a bare array of slot rows, every slot of the day, past or not (backend.md §4.D; reservation_controller.js:79-93, :512-543; reservation_service.js:598-668)",
  }),
  /** the day's bookings, one row per service line */
  filter: route<WireDayFilter, WireReservationRow[]>("POST", "/reservation/filter", gated({ modules: ["Schedule", "Sale", "Order", "Support"] }), {
    notes:
      "`{ date }` alone returns every booking starting or ending that day, any status, unpaged and uncapped, sorted by start; `time` + `endTime` would hide seated, completed and cancelled bookings, so v2 never sends them (backend.md O3; reservation_controller.js:101-105, :571-589; reservation_service.js:258-512)",
  }),
  /** one booking, whole, with the linked sale's status */
  fetch: route<WireFetchRequest, WireFetchedReservation>("POST", "/reservation/fetch", gated({ modules: ["Schedule", "Sale", "Support"] }), {
    notes:
      "the copy to edit and save back: it carries `version`, `eventLog` and everything else; send `branchId` for a booking of another branch than the token's (backend.md §3.J, O1; reservation_controller.js:126-131, :289-328; reservation_service.js:39-60)",
  }),
  /** create a booking, or replace one whole */
  save: route<WireReservation, WireSaveAnswer>("POST", "/reservation/save", gated({ modules: ["Schedule", "Sale", "Support"] }), {
    notes:
      "a whole-document replace: a field left out is deleted; no `version` creates, a `version` with `mid` updates, and a stale `version` (or a changed `branchId`) answers `REST_VERSION_MISMATCH`; no capacity, hours, clash or date is checked (backend.md §3.A, §3.B, §3.E, O7; reservation_controller.js:113-119, :424-502; entity_service.js:617-656, :931-1010)",
  }),
  /** cancel one line of a booking, or all of it */
  cancel: route<WireCancelRequest, WireReservation, ReservationPath>("POST", "/reservation/:reservationId/cancel", gated({ modules: ["Schedule", "Sale", "Support"] }), {
    notes:
      "needs no `version` (the backend reads and writes under its own); the answer is the whole booking with no wrapper; a repeat cancel appends another event, nothing refuses it (backend.md §2.D; reservation_controller.js:141-145, :331-370; reservation_service.js:1364-1421)",
  }),
  /** text the guest the booking link */
  sms: route<WireSmsRequest, WireSendAnswer, ReservationPath>("POST", "/reservation/:reservationId/sms", gated({ modules: ["Schedule", "Sale", "Support"] }), {
    notes:
      "`message` holds one `%s` for the link; without `hasSMSProvider` nothing is sent and the answer carries the text; the booking is never loaded, so a wrong id still answers `sent` (backend.md §5.1; reservation_controller.js:171, :695-745)",
  }),
  /** WhatsApp the guest the booking details */
  whatsapp: route<WireWhatsAppRequest, WireSendAnswer, ReservationPath>("POST", "/reservation/:reservationId/whatsapp", gated({ modules: ["Schedule", "Sale", "Support"] }), {
    notes:
      "a template through the comms forwarder; `brandId` must be a real brand (`REST_INVALID_BRAND` otherwise); `message` is required but unused; `sent` is the forwarder's flag (backend.md §5.1; reservation_controller.js:183, :850-1008)",
  }),
  /** email the guest the booking link */
  linkEmail: route<WireLinkEmailRequest, unknown, ReservationPath>("POST", "/reservation/:reservationId/link/email", gated({ modules: ["Schedule", "Sale", "Support"] }), {
    notes:
      "rate-limited per session (about 10 calls a minute across both email routes, `REST_ERR_REQUEST_TEMPORARILY_SUSPENDED` after); answers an empty success (backend.md §5.1; reservation_controller.js:177, :755-848; email_rate_limiter.js)",
  }),
});
